import React from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";

import { Banner } from "@airhelp/react";

import { BANNER_CHANNEL } from "config/channels";
import { PAGE_PATHS } from "config/routes";
import { getMyProfile } from "hooks/api/userProfiles/useFetchMyProfile/useFetchMyProfile";
import { Offer } from "models/Offers";

import { isCompleteAnnualMembership, isEssentialAnnualMembership } from "../membershipHelper";

const MembershipCheckoutBanner = ({ productDetails }: { productDetails: Offer }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userProfile = getMyProfile();
  const policy = userProfile?.policy;
  const [searchParams] = useSearchParams();

  if (!policy || !policy.membership || !productDetails) {
    return;
  }

  const isEssentialProduct = isEssentialAnnualMembership(productDetails.name);
  const isCompleteProduct = isCompleteAnnualMembership(productDetails.name);

  const isMembershipComplete = policy.membership.isComplete;
  const isMembershipEssential = policy.membership.isEssential;

  const getBannerLabel = () => {
    if (isMembershipComplete) {
      return t("memberships.no_need_to_purchase_this_subscription", { plan: t("pricing.plans.complete") });
    }
    if (isMembershipEssential && isEssentialProduct) {
      return t("memberships.upgrade_to_complete", { plan: t("pricing.plans.essential") });
    }
    return;
  };

  const getButtonLabel = () => {
    if (isMembershipComplete) {
      return t("common.continue_to_just_dashboard");
    }
    if (isMembershipEssential && isEssentialProduct) {
      return t("common.upgrade");
    }
    return;
  };

  const onButtonAction = () => {
    if (isMembershipComplete) {
      return navigate(generatePath(PAGE_PATHS.HOME_PAGE));
    }
    if (isMembershipEssential && isEssentialProduct) {
      searchParams.set("channel", BANNER_CHANNEL);
      navigate({ pathname: generatePath(PAGE_PATHS.MEMBERSHIPS.BUY.EMAIL_PAGE), search: searchParams.toString() });
      // TODO - remove this when we have a proper solution for not allowing 2 memberships
      return navigate(0);
    }
    return;
  };

  if (isMembershipEssential && isCompleteProduct) {
    return;
  }

  return (
    <Banner
      variant="warning"
      title={getBannerLabel()}
      width="100%"
      buttonLabel={getButtonLabel()}
      buttonAction={onButtonAction}
    />
  );
};

export default MembershipCheckoutBanner;
